import React, { useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { Eye, EyeOff, Lock, ArrowRight } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import Logo from "@/components/Logo";

export default function OfficialLogin() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [show, setShow] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!email || !password) return setError("Veuillez remplir les deux champs.");
    setLoading(true);
    try {
      const user = await login(email, password);
      if (user.role !== "official") {
        setError("Ce compte n'est pas un compte officiel. Utilise /login.");
        return;
      }
      const next = params.get("next") || "/officiel";
      navigate(next, { replace: true });
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F5F5F0] flex items-center justify-center p-6" data-testid="official-login-page">
      <div className="w-full max-w-md">
        <div className="flex items-center gap-3 mb-8">
          <Logo size={40} />
          <span className="ml-2 badge-dark">Officiel</span>
        </div>
        <div className="card-flat">
          <div className="label mb-1">Accès officiel</div>
          <h2 className="font-serif text-4xl mb-2">Connexion.</h2>
          <p className="text-sm text-[var(--muted)] mb-6 leading-relaxed">
            Connecte-toi pour répondre aux demandes de disponibilités et voir tes parties assignées.
          </p>
          <form onSubmit={submit} className="space-y-5">
            {params.get("expired") === "1" && (
              <div
                className="border border-black p-3 text-sm leading-snug"
                style={{ background: "var(--surface-3)" }}
                data-testid="official-login-session-expired-banner"
              >
                <span className="font-bold">⏱ </span>
                Session expirée pour inactivité. Reconnecte-toi pour continuer.
              </div>
            )}
            <div>
              <label className="label block mb-2">Courriel</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={loading}
                autoFocus
                className="input-line"
                data-testid="official-email-input"
              />
            </div>
            <div>
              <label className="label block mb-2">Mot de passe</label>
              <div className="relative">
                <input
                  type={show ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  disabled={loading}
                  className="input-line pr-10"
                  data-testid="official-password-input"
                />
                <button type="button" onClick={() => setShow(!show)} className="absolute right-0 top-2 text-[var(--muted)] hover:text-black">
                  {show ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>
            {error && (
              <div className="border-2 border-black p-3 text-xs bg-conflict-stripes-light font-mono" data-testid="official-login-error">
                {error}
              </div>
            )}
            <button type="submit" disabled={loading} className="btn-primary w-full justify-center" data-testid="official-login-submit-btn">
              <Lock size={14} strokeWidth={2.5} />
              {loading ? "Vérification…" : "Se connecter"}
            </button>
          </form>

          <p className="mt-6 pt-5 border-t border-[var(--border)] font-mono text-[10px] uppercase tracking-widest text-[var(--muted)]">
            <Link to="/officiel/mot-de-passe-oublie" className="hover:text-black underline" data-testid="official-forgot-password-link">
              Mot de passe oublié ? Recevoir un SMS
            </Link>
          </p>
          <p className="mt-4 font-mono text-[10px] uppercase tracking-widest text-[var(--muted)]">
            Pas encore de compte? Demande le lien d'inscription à ton gestionnaire.
          </p>
        </div>
        <div className="mt-4 text-center">
          <Link to="/login" className="font-mono text-[10px] uppercase tracking-widest text-[var(--muted)] hover:text-black inline-flex items-center gap-1">
            Accès gestionnaire <ArrowRight size={11} strokeWidth={2.5} />
          </Link>
        </div>
      </div>
    </div>
  );
}
